/**
 * Tiny RFC 5545 VCALENDAR builder. Pure function — no deps, no Date prototype
 * mutations. Used by the EventBlock on the post detail page to ship a member's
 * "Add to my calendar" download.
 *
 * Scope: one VEVENT per call. Times are emitted in UTC form
 * (YYYYMMDDTHHMMSSZ) so the consuming calendar app converts to the user's
 * local zone. End time defaults to start + 1 hour when not provided.
 *
 * Escaping per spec:
 *   backslash → \\
 *   ;         → \;
 *   ,         → \,
 *   newline   → \n
 *
 * Long-line folding per RFC 5545 §3.1: lines longer than 75 octets must be
 * split, with the continuation line beginning with a single space. We count
 * UTF-8 octets, not JS string length, because non-ASCII chars (e.g. accented
 * venue names) take >1 octet each.
 */

export interface BuildIcsArgs {
  title: string;
  startUtc: string;        // ISO 8601
  endUtc: string | null;   // ISO 8601; defaults to start + 1h when null
  description?: string;
  url?: string;
  location?: string;
  uid: string;             // stable identifier; we use the post slug
}

function escapeText(s: string): string {
  // Order matters: escape the backslash first so we don't re-escape the
  // backslashes we just introduced.
  return s
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r\n|\n|\r/g, '\\n');
}

function toIcsUtc(iso: string): string {
  // UTC-form date-time per RFC 5545 §3.3.5: YYYYMMDDTHHMMSSZ. Date parses ISO
  // strings into the absolute instant; the UTC getters give us the right slug
  // regardless of the runtime timezone.
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}

function foldLine(line: string): string {
  // RFC 5545 §3.1: wrap at 75 octets, continue with CRLF + single space. We
  // walk the string by codepoint and accumulate octets so the split lands on
  // a character boundary.
  const encoder = new TextEncoder();
  const max = 75;
  const parts: string[] = [];
  let current = '';
  let currentBytes = 0;
  for (const ch of line) {
    const chBytes = encoder.encode(ch).length;
    if (currentBytes + chBytes > max) {
      parts.push(current);
      current = ch;
      currentBytes = chBytes;
    } else {
      current += ch;
      currentBytes += chBytes;
    }
  }
  if (current.length > 0) parts.push(current);
  return parts.join('\r\n ');
}

export function buildIcs(args: BuildIcsArgs): string {
  const start = toIcsUtc(args.startUtc);
  const endIso = args.endUtc
    ?? new Date(new Date(args.startUtc).getTime() + 60 * 60 * 1000).toISOString();
  const end = toIcsUtc(endIso);
  const dtstamp = toIcsUtc(new Date().toISOString());

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Converted Closet//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${escapeText(args.uid)}@convertedcloset`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeText(args.title)}`,
  ];
  if (args.description) lines.push(`DESCRIPTION:${escapeText(args.description)}`);
  if (args.url)         lines.push(`URL:${escapeText(args.url)}`);
  if (args.location)    lines.push(`LOCATION:${escapeText(args.location)}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');

  // CRLF line endings per RFC 5545 §3.1. Fold each content line.
  return lines.map(foldLine).join('\r\n') + '\r\n';
}

export function downloadIcs(filename: string, ics: string): void {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
